import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import qs from 'qs';
import { Modal, ModalHeader, ModalBody, Button } from 'reactstrap';
import { changeModalState, showNotifySuccess } from '../../modules/actions';
import { getModalState } from '../../modules/selectors';
import { getTranslationKey } from '../../modules/utils';
import { StripeElements } from '../../components/Stripe/StripeElements';
import {
  StripePaymentElement,
  usePaymentMethodSetup
} from '../../components/Stripe/StripePaymentElement';
import { updateAddonsAction } from '../../modules/actions/paymentInfo';
import {
  addPromotionCodeToSubscription,
  restartSubscription
} from '../../modules/actions/subscription';
import {
  clearPendingPaymentSetupAction,
  takePendingPaymentSetupAction,
  PendingReduxActionType
} from '../../modules/utils/pendingPaymentSetupAction';

export const PAYMENT_SETUP_SUCCESS_PARAM = 'payment_setup';

/** Registry of resumable redux actions — add an entry here for new endpoints. */
const PENDING_ACTION_CREATORS: Record<PendingReduxActionType, (payload: any) => any> = {
  updateAddons: updateAddonsAction,
  addPromotionCode: addPromotionCodeToSubscription,
  restartSubscription
};

const buildReturnUrl = (marker: string): string => {
  const { href } = window.location;
  const separator = href.includes('?') ? '&' : '?';

  return `${href}${separator}${marker}`;
};

/**
 * After the payment-setup redirect returns with ?payment_setup=success,
 * resumes the purchase the user originally attempted (stored before the
 * redirect). A failed setup return clears the stored intent instead.
 */
const usePaymentSetupResume = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    const params = qs.parse(location.search.substring(1));

    if (params.setup_failed === 'true') {
      clearPendingPaymentSetupAction();

      return;
    }

    if (params[PAYMENT_SETUP_SUCCESS_PARAM] !== 'success') return;

    const { [PAYMENT_SETUP_SUCCESS_PARAM]: ignored, ...restParams } = params;
    const search = qs.stringify(restParams);
    history.replace({ ...location, search: search ? `?${search}` : '' });

    const pending = takePendingPaymentSetupAction();
    if (!pending) return;

    dispatch(
      showNotifySuccess({
        message: getTranslationKey('subscription.paymentMethodSetupRequired.resumeNotification')
      })
    );

    if (pending.kind === 'redux-action') {
      const actionCreator = PENDING_ACTION_CREATORS[pending.actionType];
      if (actionCreator) dispatch(actionCreator(pending.payload));

      return;
    }

    if (pending.kind === 'update-addons-modal') {
      dispatch(
        changeModalState({
          key: 'updateAddonsModal',
          state: true,
          modalData: {
            ...pending.modalData,
            prefill: pending.formState,
            autoSubmit: true
          }
        })
      );
    }
  }, []);
};

const SetupForm = () => {
  const { confirmSetup, confirming, errorMessage } = usePaymentMethodSetup({
    returnURLFail: window.location.href,
    returnURLSuccess: buildReturnUrl(`${PAYMENT_SETUP_SUCCESS_PARAM}=success`)
  });

  return (
    <>
      <StripePaymentElement errorMessage={errorMessage} />
      <Button
        data-cy="PaymentMethodSetupRequiredConfirm"
        color="primary"
        className="w-100-p mt-3"
        disabled={confirming}
        onClick={confirmSetup}
        type="submit"
      >
        {confirming
          ? getTranslationKey('buttonLoading')
          : getTranslationKey('subscription.paymentMethodSetupRequired.button')}
      </Button>
    </>
  );
};

/**
 * Prompts the user to set up a new payment method for off-session use.
 * Open it whenever an API call fails with PaymentMethodSetupRequiredError
 */
export const PaymentMethodSetupRequiredModal = () => {
  const dispatch = useDispatch();
  const isOpen = useSelector(getModalState)('paymentMethodSetupRequiredModal');
  usePaymentSetupResume();

  const toggle = () => {
    // Closing without completing setup abandons the stored purchase intent.
    if (isOpen) clearPendingPaymentSetupAction();
    dispatch(changeModalState({ key: 'paymentMethodSetupRequiredModal', state: !isOpen }));
  };

  return (
    <Modal centered isOpen={isOpen} toggle={toggle} wrapClassName="modal-dark" size="md">
      <ModalHeader toggle={toggle}>
        {getTranslationKey('subscription.paymentMethodSetupRequired.title')}
      </ModalHeader>
      <ModalBody>
        <p>{getTranslationKey('subscription.paymentMethodSetupRequired.text')}</p>
        {isOpen && (
          <StripeElements mode="setup">
            <SetupForm />
          </StripeElements>
        )}
      </ModalBody>
    </Modal>
  );
};

export default PaymentMethodSetupRequiredModal;
